/**
 * What counts as a campaign (item 3). The backend writes a campaign element for any group of
 * actions, even one; the console calls it a campaign only when it joins two or more incidents. A
 * group of one is a lone incident, and a group whose incidents are not in the tenant is nothing.
 * The feed and the Overview counters both group through here, so they cannot disagree.
 */
import { campaignMemberActionIds, sourceRefId } from '../../lib/funnel';
import type { ThreatElementDocument, ThreatEventItem } from '../domain/types';

export const MIN_CAMPAIGN_INCIDENTS = 2;

export interface CampaignGroup {
  campaign: ThreatElementDocument;
  /** The campaign's actions whose incident exists, in the order the campaign lists them. */
  actions: ThreatElementDocument[];
}

export function groupCampaigns(
  elements: ThreatElementDocument[],
  threatEvents: Pick<ThreatEventItem, '_id'>[]
): CampaignGroup[] {
  const eventIds = new Set(threatEvents.map((event) => event._id));
  const actionById = new Map<string, ThreatElementDocument>();
  for (const el of elements) {
    if (String(el.element_type || '').toLowerCase() === 'action' && el._id) actionById.set(el._id, el);
  }
  const groups: CampaignGroup[] = [];
  for (const campaign of elements) {
    if (String(campaign.element_type || '').toLowerCase() !== 'campaign') continue;
    const actions = campaignMemberActionIds(campaign)
      .map((id) => actionById.get(id))
      .filter((action): action is ThreatElementDocument =>
        Boolean(action && eventIds.has(sourceRefId(action.threat_event_id)))
      );
    if (actions.length >= MIN_CAMPAIGN_INCIDENTS) groups.push({ campaign, actions });
  }
  return groups;
}
